import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Base from "../admin_components/Admin_Base";
import { Button, Card, Row, Col, Form, Alert } from "react-bootstrap";
import { Breadcrumbs } from "@mui/material";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import authAxios from "../Auth/useAxios";
import { ADMIN_API_BASE_URL } from "../Auth/Api";
import { ClipLoader } from "react-spinners";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Swal from "sweetalert2";

const EmployeeEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [formData, setFormData] = useState({});
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchEmployeeDetails();
    fetchAllEmployees();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    try {
      const response = await authAxios.get(
        `${ADMIN_API_BASE_URL}/main/admin/employee-edit/${id}`
      );
      setEmployee(response.data);
      // Format the data for form
      setFormData({
        fullName: response.data.fullName || '',
        dateOfBirth: response.data.dateOfBirth || '',
        email: response.data.email || '',
        mobile: response.data.mobile || '',
        location: response.data.location || '',
        designation: response.data.designation || '',
        address: response.data.address || '',
        username: response.data.username || '',
        documentName: response.data.documentName || '',
        managerReferenceId: response.data.manager?.employeeReferenceId || '',
        active: response.data.active || true
      });
    } catch (error) {
      console.error("Error fetching employee details:", error);
      setError("Failed to fetch employee details");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const response = await authAxios.get(
        `${ADMIN_API_BASE_URL}/main/admin/list-employee`
      );
      setAllEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const submitData = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      const response = await authAxios.put(
        `${ADMIN_API_BASE_URL}/main/admin/update-employee/${id}`,
        submitData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSuccess("Employee updated successfully!");
      Swal.fire({
        title: "Success!",
        text: "Employee details have been updated successfully.",
        icon: "success",
        confirmButtonColor: "#3085d6",
      });
      
      // Navigate back to view page after success
      setTimeout(() => {
        navigate(`/admin/employee-view/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error("Error updating employee:", error);
      const errorMessage = error.response?.data?.message || error.response?.data || "Failed to update employee details";
      setError(errorMessage);
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/employee-view/${id}`);
  };

  const handleBack = () => {
    navigate("/admin/admin_employee_list");
  };

  // Filter out current employee from manager list to avoid self-assignment
  const availableManagers = allEmployees.filter(emp => 
    emp.id !== parseInt(id) && emp.active === true
  );

  if (loading) {
    return (
      <Base>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "300px" }}>
          <ClipLoader color="darkslategrey" size={50} />
        </div>
      </Base>
    );
  }

  if (error && !employee) {
    return (
      <Base>
        <div className="alert alert-danger text-center">{error}</div>
      </Base>
    );
  }

  return (
    <Base>
      <div className="container-fluid pt-3 mt-5">
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="/admin/dashboard" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
            Dashboard
          </Link>
          <Link underline="hover" color="inherit" href="/admin/admin_employee_list" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
            Employee Management
          </Link>
          <Link underline="hover" color="inherit" href={`/admin/employee-view/${id}`} sx={{ color: "darkslategrey", fontWeight: "bold" }}>
            View Employee
          </Link>
          <Link underline="none" color="inherit" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
            Edit Employee
          </Link>
        </Breadcrumbs>
      </div>

      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold text-dark mb-1">Edit Employee</h2>
            <p className="text-muted mb-0">Update employee information and hierarchy</p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleBack}>
              <ArrowBackIcon className="me-1" /> Back to List
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row>
            {/* Personal Information */}
            <Col md={8}>
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-light border-0">
                  <Card.Title className="mb-0">Personal Information</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="fullName"
                          value={formData.fullName || ''}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Date of Birth *</Form.Label>
                        <Form.Control
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth || ''}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Email *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Username *</Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          value={formData.username || ''}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile *</Form.Label>
                        <Form.Control
                          type="tel"
                          name="mobile"
                          value={formData.mobile || ''}
                          onChange={handleChange}
                          pattern="[0-9]{10}"
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Designation *</Form.Label>
                        <Form.Select
                          name="designation"
                          value={formData.designation || ''}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Designation</option>
                          <option value="GM">General Manager</option>
                          <option value="AGM">Assistant General Manager</option>
                          <option value="DO">Development Officer</option>
                          <option value="DM">Development Manager</option>
                          <option value="MO">Marketing Officer</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Location *</Form.Label>
                        <Form.Control
                          type="text"
                          name="location"
                          value={formData.location || ''}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Manager</Form.Label>
                        <Form.Select
                          name="managerReferenceId"
                          value={formData.managerReferenceId || ''}
                          onChange={handleChange}
                        >
                          <option value="">No Manager</option>
                          {availableManagers.map((manager) => (
                            <option key={manager.id} value={manager.employeeReferenceId}>
                              {manager.fullName} ({manager.employeeReferenceId}) - {manager.designation}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {employee?.manager ? 
                            `Current: ${employee.manager.fullName} (${employee.manager.employeeReferenceId})` : 
                            'No manager currently assigned'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Address *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="address"
                      value={formData.address || ''}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* Document Information */}
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-light border-0">
                  <Card.Title className="mb-0">Document Information</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Document Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="documentName"
                          value={formData.documentName || ''}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Update Document File</Form.Label>
                        <Form.Control
                          type="file"
                          name="documentFile"
                          onChange={handleChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        />
                        <Form.Text className="text-muted">
                          {employee?.documentFile ? 
                            <a href={employee.documentFile} target="_blank" rel="noopener noreferrer">
                              View Current Document
                            </a> : 
                            'No document uploaded'
                          }
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Status */}
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-light border-0">
                  <Card.Title className="mb-0">Status</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Active Employee"
                      name="active"
                      checked={formData.active || false}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Uncheck to deactivate this employee
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            {/* Employee Information Sidebar */}
            <Col md={4}>
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-light border-0">
                  <Card.Title className="mb-0">Employee Details</Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="text-center mb-3">
                    <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" 
                         style={{ width: '80px', height: '80px', fontSize: '24px' }}>
                      {employee?.fullName?.charAt(0) || 'E'}
                    </div>
                  </div>
                  <div className="employee-info">
                    <p><strong>Reference ID:</strong> {employee?.employeeReferenceId}</p>
                    <p><strong>Current Designation:</strong> {employee?.designation}</p>
                    <p><strong>Join Date:</strong> {employee?.joined_at ? new Date(employee.joined_at).toLocaleDateString() : 'N/A'}</p>
                    <p><strong>Last Updated:</strong> {employee?.updated_at ? new Date(employee.updated_at).toLocaleDateString() : 'Never'}</p>
                  </div>
                </Card.Body>
              </Card>

              {/* Current Manager Info */}
              {employee?.manager && (
                <Card className="mb-4 shadow-sm border-0">
                  <Card.Header className="bg-light border-0">
                    <Card.Title className="mb-0">Current Manager</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Name:</strong> {employee.manager.fullName}</p>
                    <p><strong>Reference ID:</strong> {employee.manager.employeeReferenceId}</p>
                    <p><strong>Designation:</strong> {employee.manager.designation}</p>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          {/* Action Buttons */}
          <Card className="mb-4 shadow-sm border-0">
            <Card.Body className="text-center">
              <div className="d-flex justify-content-center gap-3">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCancel}
                  disabled={saving}
                  size="lg"
                >
                  <CancelIcon className="me-1" /> Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={saving}
                  size="lg"
                >
                  {saving ? (
                    <>
                      <ClipLoader size={20} color="#fff" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <SaveIcon className="me-1" /> Update Employee
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Form>
      </div>
    </Base>
  );
};

export default EmployeeEdit;